import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, User, Heart, ShoppingBag, Menu, X } from 'lucide-react';
import CartPage from '../pages/CartPage';
import { getUnifiedCart, syncUserCartCache } from '../utils/cartHelper';

const Navbar = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [cartItems, setCartItems] = useState(() => getUnifiedCart());

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) setUser(JSON.parse(storedUser));
  }, []);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
    }
  }, [location]);

  useEffect(() => {
    const initCart = async () => {
      const storedUser = localStorage.getItem('user');
      const currentUser = storedUser ? JSON.parse(storedUser) : null;
      if (currentUser?.id) {
        const items = await syncUserCartCache(currentUser.id);
        setCartItems(items);
      } else {
        setCartItems(getUnifiedCart());
      }
    };

    initCart();

    const handleCartSync = () => {
      setCartItems(getUnifiedCart());
    };

    window.addEventListener('vanya_cart_change', handleCartSync);
    window.addEventListener('storage', handleCartSync);
    return () => {
      window.removeEventListener('vanya_cart_change', handleCartSync);
      window.removeEventListener('storage', handleCartSync);
    };
  }, [user]);

  const updateQty = (id, newQty) => {
    if (newQty < 1) return;
    setCartItems(cartItems.map(item => item.id === id ? { ...item, quantity: newQty } : item));
  };

  const removeItem = (id) => {
    setCartItems(cartItems.filter(item => item.id !== id));
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
    setIsDropdownOpen(false);
  };

  return (
    <div style={styles.container}>
      <nav className="navbar-layout" style={styles.navbar}>
        
        {/* 1. Logo Section */}
        <div className="logo-section" style={styles.logoSection}>
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', height: '100%' }}>
            <img src="/vanyalogo.png" alt="Vanya Logo" style={styles.logoImage} />
          </Link>
        </div>

        {/* Desktop Links */}
        <div className="nav-links-desktop" style={styles.navLinks}>
          {[
            { label: 'HOME', path: '/' },
            { label: 'SHOP', path: '/shop' },
            { label: 'COLLECTIONS', path: '/collection' },
            { label: 'TRACK ORDER', path: '/track-order' },
            { label: 'ABOUT', path: '/about' },
            { label: 'CONTACT', path: '/contact' },
          ].map((item) => {
            const isCurrentActive = item.path === '/' 
              ? (location.pathname === '/' || location.pathname === '') 
              : location.pathname.toLowerCase().startsWith(item.path.toLowerCase());
            return (
              <Link
                key={item.path}
                to={item.path}
                style={isCurrentActive ? styles.activeLink : styles.link}
                className={`nav-link-item ${isCurrentActive ? 'active-nav-link' : ''}`}
              >
                {isCurrentActive && <span className="active-nav-sparkle" style={{ marginRight: '6px', fontSize: '10px', color: '#f3c653' }}>✦</span>}
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>

        {/* 2. Action Icons */}
        <div className="icon-group-mobile" style={styles.iconGroup}>
          <Search size={20} style={styles.icon} />
          
          {user ? (
            <div style={{ position: 'relative' }}>
              <button style={styles.userDropdownBtn} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                <User size={22} style={styles.icon} />
              </button>
              {isDropdownOpen && (
                <div style={styles.userDropdownMenu}>
                  <button
                    onClick={() => {
                      navigate("/ProfilePage");
                      setIsDropdownOpen(false);
                    }}
                    style={styles.dropdownItemBtn}
                  >
                    My Profile
                  </button>
                  <button
                    onClick={handleLogout}
                    style={styles.dropdownItemBtn}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/AuthPage" style={{ display: 'flex', alignItems: 'center' }}><User size={22} style={styles.icon} /></Link>
          )}
          
          <div
            style={{ cursor: "pointer", position: "relative", display: 'flex', alignItems: 'center' }}
            onClick={() => navigate("/wishlist")}
          >
            <Heart size={20} style={styles.icon} />
          </div>         
          
          <div style={{ position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => setIsCartOpen(true)}>
            <ShoppingBag size={22} style={styles.icon} />
            <span style={styles.badge} className="nav-cart-badge">
              {cartItems.reduce((acc, item) => acc + (Number(item.quantity) || 1), 0)}
            </span>
          </div>
        </div>

        {/* 3. Mobile / Android Hamburger Toggle */}
        <div className="mobile-toggle" style={styles.mobileToggle} onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </div>
      </nav>

      {/* Mobile & Android Slide Menu Overlay */}
      {isMenuOpen && (
        <div style={styles.mobileMenu}>
          {[
            { label: 'HOME', path: '/' },
            { label: 'SHOP', path: '/shop' },
            { label: 'COLLECTIONS', path: '/collection' },
            { label: 'TRACK ORDER', path: '/track-order' },
            { label: 'ABOUT', path: '/about' },
            { label: 'CONTACT', path: '/contact' },
          ].map((item) => {
            const isCurrentActive = item.path === '/' 
              ? (location.pathname === '/' || location.pathname === '') 
              : location.pathname.toLowerCase().startsWith(item.path.toLowerCase());
            return (
              <Link
                key={item.path}
                to={item.path}
                style={isCurrentActive ? styles.activeMobileNavLink : styles.mobileNavLink}
                onClick={() => setIsMenuOpen(false)}
              >
                <span>{item.label}</span>
                {isCurrentActive && <span style={{ color: '#f3c653', fontSize: '13px' }}>✦</span>}
              </Link>
            );
          })}
        </div>
      )}

      <CartPage 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)} 
        cartItems={cartItems}
        onUpdateQty={updateQty}
        onRemove={removeItem}
      />

      <style>
        {`
          @media (max-width: 992px) {
            .navbar-layout {
              display: grid !important;
              grid-template-columns: 1fr auto auto !important; 
              gap: 12px;
              padding: 0 12px !important;
              align-items: center;
              height: 60px !important;
            }
            .nav-links-desktop { display: none !important; }
            
            .logo-section {
              justify-content: flex-start !important;
              margin-right: 0 !important;
            }

            .logo-section img {
              height: 48px !important;
            }

            .icon-group-mobile {
              justify-content: flex-end !important;
              gap: 14px !important; 
            }

            .mobile-toggle { 
              display: flex !important; 
              justify-content: flex-end;
              align-items: center;
            }
          }

          .logo-section a:hover img {
            transform: scale(1.05);
          }

          .nav-link-item {
            transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1) !important;
            position: relative;
          }
          .nav-link-item:hover {
            color: #fceea7 !important;
            background: rgba(255, 255, 255, 0.12) !important;
            transform: translateY(-1px);
          }
          .active-nav-link {
            color: #ffdf79 !important;
            background: linear-gradient(135deg, rgba(246, 211, 101, 0.24) 0%, rgba(201, 155, 39, 0.16) 100%) !important;
            border: 1.5px solid #e2bc53 !important;
            box-shadow: 0 0 16px rgba(226, 188, 83, 0.35), inset 0 1px 2px rgba(255, 255, 255, 0.3) !important;
          }
          .active-nav-sparkle {
            font-size: 10px;
            color: #f3c653;
            animation: pulseSparkle 2s infinite ease-in-out;
          }
          @keyframes pulseSparkle {
            0%, 100% { opacity: 0.8; transform: scale(0.9); }
            50% { opacity: 1; transform: scale(1.15); }
          }
          .nav-cart-badge {
            animation: badgePop 0.3s ease;
          }
          @keyframes badgePop {
            0% { transform: scale(0.7); }
            70% { transform: scale(1.15); }
            100% { transform: scale(1); }
          }
        `}
      </style>
    </div>
  );
};

const styles = {
  container: {
    fontFamily: "'Playfair Display', serif",
    width: '100%',
    position: 'sticky',
    top: 0,
    zIndex: 2000,
  },
  logoImage: {
    height: '58px', 
    width: 'auto',
    display: 'block',
    objectFit: 'contain',
    borderRadius: '50%',
    filter: 'drop-shadow(0 3px 8px rgba(0, 0, 0, 0.45))',
    transition: 'transform 0.25s ease',
  },
  logoSection: { 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'flex-start',
    padding: '0',
    height: '100%', 
  },
  navbar: {
    backgroundColor: '#522b5b', 
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 5%', 
    height: '70px', 
    color: '#f3c653', 
    boxShadow: '0 4px 15px rgba(0,0,0,0.25)',
  },
  navLinks: { display: 'flex', gap: '16px', alignItems: 'center' },
  link: { 
    color: '#fdf8f2', 
    textDecoration: 'none', 
    fontSize: '13px', 
    fontWeight: '500', 
    letterSpacing: '1.2px', 
    padding: '7px 16px',
    borderRadius: '999px',
    transition: 'all 0.25s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeLink: {
    color: '#ffdf79',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: '700',
    letterSpacing: '1.2px',
    padding: '7px 18px',
    borderRadius: '999px',
    background: 'linear-gradient(135deg, rgba(246, 211, 101, 0.24) 0%, rgba(201, 155, 39, 0.16) 100%)',
    border: '1.5px solid #e2bc53',
    boxShadow: '0 0 16px rgba(226, 188, 83, 0.35), inset 0 1px 2px rgba(255, 255, 255, 0.3)',
    transition: 'all 0.25s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  iconGroup: { display: 'flex', gap: '20px', alignItems: 'center', color: '#f3c653' },
  icon: { cursor: 'pointer', strokeWidth: '1.5px', color: '#f3c653' },
  mobileToggle: { display: 'none', color: '#f3c653', cursor: 'pointer' },
  mobileMenu: { 
    position: 'absolute', 
    top: '100%', 
    left: 0, 
    width: '100%', 
    backgroundColor: '#522b5b', 
    display: 'flex', 
    flexDirection: 'column', 
    zIndex: 999, 
    borderTop: '1px solid #63366e',
    boxShadow: '0 10px 20px rgba(0,0,0,0.3)'
  },
  mobileNavLink: { 
    color: '#fdf8f2', 
    padding: '16px 6%', 
    textDecoration: 'none', 
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)', 
    fontSize: '14px',
    letterSpacing: '1px',
    fontWeight: '500',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  activeMobileNavLink: {
    color: '#ffdf79',
    padding: '16px 6%',
    textDecoration: 'none',
    borderBottom: '1px solid rgba(243, 198, 83, 0.3)',
    background: 'linear-gradient(90deg, rgba(243, 198, 83, 0.2) 0%, rgba(243, 198, 83, 0.05) 100%)',
    fontSize: '14px',
    letterSpacing: '1px',
    fontWeight: '700',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  badge: {
    position: 'absolute',
    top: '-7px',
    right: '-9px',
    background: 'linear-gradient(135deg, #ffe57f 0%, #e2bc53 100%)',
    color: '#1a0524',
    fontSize: '11.5px',
    fontWeight: '900',
    fontFamily: '"Montserrat", sans-serif',
    borderRadius: '50%',
    width: '21px',
    height: '21px',
    minWidth: '21px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #522b5b',
    boxShadow: '0 2px 7px rgba(0, 0, 0, 0.5), 0 0 8px rgba(226, 188, 83, 0.6)',
    zIndex: 10,
    pointerEvents: 'none',
    lineHeight: 1,
    boxSizing: 'border-box',
  },
  userDropdownBtn: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' },
  userDropdownMenu: { 
    position: 'absolute', 
    top: 'calc(100% + 10px)', 
    right: 0, 
    backgroundColor: '#4d2456', 
    border: '1px solid rgba(226, 188, 83, 0.3)', 
    borderRadius: '12px', 
    minWidth: '140px', 
    boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
    overflow: 'hidden',
    zIndex: 1000
  },
  dropdownItemBtn: { 
    background: 'none', 
    border: 'none', 
    color: '#fdf8f2', 
    padding: '12px 18px', 
    cursor: 'pointer', 
    width: '100%', 
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'background 0.2s, color 0.2s'
  }
};

export default Navbar;